import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import DashboardCreateSelectNetworkModal from "../modal/SelectNetworks";
import { Device, NetworkSelected, NetworkType } from "types";
import SelectedList, { TableColumn } from "./SelectedList";
import Icon from "@mui/material/Icon";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    ColorPicker,
    CreateChartState,
    useCreateDashboardStore,
} from "..";
import { ChartSource, ChartType } from "constant/chart";
import { BULK_CREATE_WIDGET_MESSAGE, DASHBOARD_CHART_MAX } from "constant";
import { ColorPickerBackground } from "components/common";
import { getMQTTQueryByDevice } from "components/dashboard/utils/widgetUtil";
import { getWidgetConfigByWidgetType, WidgetType } from "components/dashboard/utils/widgetConfig";

const getCreateData = (networkType: NetworkType): CreateChartState => {
    const getRoomZoneGroupName = () => {
        switch (networkType) {
            case NetworkType.ROOM:
                return "Room";
            case NetworkType.ZONE:
                return "Zone";
            case NetworkType.GROUP:
                return "Group";
            default:
                return "Room";
        }
    };

    return {
        title: "Creating New Widget(s)",
        data: [],
        columns: [
            {
                key: "widgetName",
                name: "Widget Name",
            },
            {
                key: "deviceName",
                name: getRoomZoneGroupName(),
            },
            {
                key: "gatewayName",
                name: "Network Name",
            },
            {
                key: "gatewayId",
                name: "Network ID",
            },
            {
                key: "color",
                name: "Colors",
                render: (data: any) => (
                    <div className="color-picker__toggle justify-content-start gap-1">
                        {data.color && Array.isArray(data.color) ? (
                            data.color.map((c: string, idx: number) => (
                                <ColorPickerBackground
                                    className="mr-2"
                                    key={idx + c}
                                    $inputColor={c}
                                />
                            ))
                        ) : (
                            <ColorPickerBackground
                                className="mr-2"
                                $inputColor={data.color as string}
                            />
                        )}
                    </div>
                ),
            },
        ],
        transform: (data: NetworkSelected) => ({
            attributes: [{
                color: data.color,
                device_id: data.ldsuUID,
                gateway_id: data.gatewayId,
                name: data.deviceType,
                time_series: getMQTTQueryByDevice(data),
                said: 0,
                unit: "",
                show_alert: false,
            }],
            source: ChartSource.SINGLE_SENSOR,
            name: data.widgetName ?? data.name,
            chart: data.deviceType === NetworkType.ROOM ? ChartType.MULTIPLE_LINES : ChartType.SINGLE_STEP_LINE,
        }),
        validate() {
            return this.data.map((item) => ({
                path: `[name="${item.deviceId}.widgetName"]`,
                isValid: CHART_NAME_PATTERN.test(item.widgetName.trim()),
                message: CHART_ERROR_MESSAGE,
            }));
        },
    };
};

const NetworkTypeMap: Partial<Record<WidgetType, NetworkType>> = {
    [WidgetType.PSL_CHART_ROOM_TIMELINE]: NetworkType.ROOM,
    [WidgetType.PSL_CHART_ZONE_TIMELINE]: NetworkType.ZONE,
    [WidgetType.PSL_CHART_GROUP_TIMELINE]: NetworkType.GROUP,
};

const getColumns = (networkType: NetworkType): TableColumn<NetworkSelected>[] => [
    {
        key: "deviceName",
        label: (_item) => {
            switch (networkType) {
                case NetworkType.ROOM:
                    return "Room Name";
                case NetworkType.ZONE:
                    return "Zone Name";
                case NetworkType.GROUP:
                    return "Group Name";
                default:
                    return "Room Name";
            }
        },
        className: "text-truncate col-2"
    },
    {
        key: "widgetName",
        label: "Widget Name",
        className: "col-2",
        render: (item, props) => (
            <Form.Control
                name="widget-name"
                type="text"
                placeholder="Widget Name"
                value={item.widgetName}
                onChange={(event) => props?.onChange?.(item, { widgetName: event.target.value })}
            />
        ),
    },
    { key: "gatewayName", label: "Network Name", className: "text-truncate col-3" },
    { key: "gatewayId", label: "Network ID", className: "text-truncate col-3" },
    {
        key: "color",
        label: "Colors",
        className: "col-1",
        render: (item, props) => (
            <div className="d-flex align-items-center">
                <ColorPicker
                    defaultValue={item.color as string}
                    onChange={(e, color) =>
                        props?.onChange?.(item, { color: color })
                    }
                />
            </div>
        ),
    },
    {
        key: "actions",
        label: "",
        render: (item, props) => (
            <Icon
                role="button"
                className="cursor-pointer mr-2 text-primary-red-1"
                onClick={() => props?.onRemove?.(item)}
            >
                close
            </Icon>
        ),
        className: "col-1 text-end",
    },
];

const CreateTimelineWidget = () => {
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedNetworks, setSelectedNetworks] = useState<
        Record<string, NetworkSelected>
    >({});
    const [networkType, setNetworkType] = useState(NetworkType.ROOM);
    const { widget } = useCreateDashboardStore();

    // Get widgetType from widget, defaulting to ROOM_TIMELINE if not available
    const widgetType = widget?.widgetType ?? WidgetType.PSL_CHART_ROOM_TIMELINE;
    const widgetConfiguration = getWidgetConfigByWidgetType(widgetType);

    const networks = Object.values(selectedNetworks);

    useEffect(() => {
        setNetworkType(NetworkTypeMap[widgetType ?? WidgetType.PSL_CHART_ROOM_TIMELINE]!);
    }, [widgetType]);

    useEffect(() => {
        useCreateDashboardStore.setState({
            createData: {
                ...getCreateData(networkType),
                data: Object.values(selectedNetworks),
                multiple: true,
            },
        });
    }, [selectedNetworks, networkType, widgetConfiguration]);

    const onSelectNetwork = (networks: Record<string, Device>) => {
        const selected: Record<string, NetworkSelected> = Object.values(
            networks
        )
            .map((item: NetworkSelected) => ({
                ...item,
                widgetName: item.deviceName,
                color: "#E86037", // assign color
            }))
            .pluck("deviceId");
        setSelectedNetworks(selected);
    };

    const onRemove = (device: NetworkSelected) => {
        const selected = { ...selectedNetworks };
        delete selected[device.deviceId];
        setSelectedNetworks(selected);
    };

    const onChange = (
        network: NetworkSelected,
        changes: Partial<NetworkSelected>
    ) => {
        const networks_ = { ...selectedNetworks };
        const networkChanges_ = { ...network, ...changes };
        networks_[network.deviceId] = networkChanges_;
        setSelectedNetworks(networks_);
    };

    return (
        <>
            <Row>
                <Col sm="12">
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            variant="primary"
                            className="mr-2"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select {networkType.capitalize() + "s"}
                        </Button>
                    </div>
                    {!widgetConfiguration.isCombined && (
                        <p className="reusable-description mt-2">
                            {BULK_CREATE_WIDGET_MESSAGE.fill({
                                type: networkType + "s",
                            })}
                        </p>
                    )}
                    <DashboardCreateSelectNetworkModal
                        show={showSelectModal}
                        maxSelected={widgetConfiguration.maxItems ?? DASHBOARD_CHART_MAX}
                        onHide={() => setShowSelectModal(false)}
                        selected={selectedNetworks}
                        networkType={networkType}
                        onSave={(event, data) => {
                            onSelectNetwork(data);
                        }}
                    />
                </Col>
            </Row>
            <Row className="mt-4">
                {networks.length > 0 && (
                    <SelectedList
                        data={networks}
                        onRemove={onRemove}
                        onChange={onChange}
                        columns={getColumns(networkType)}
                    />
                )}
            </Row>
        </>
    );
};

export default CreateTimelineWidget;
